import type {
  HomepageLayoutSection,
  HomepageSectionSettings,
} from '../components/sections/sectionRegistry';

export type SectionLayoutMode = 'composition' | 'flow';

type SectionLike = Pick<HomepageLayoutSection, 'type' | 'settings'> | null | undefined;

const FLOW_ROOT_BLOCK_TYPES = new Set([
  'gallery',
  'product-card',
  'request-form',
  'site-progress',
  'site-footer',
  'customiser-viewer',
  'customiser-finish-picker',
  'customiser-colour-filter',
  'customiser-combination-results',
  'product-deconstructor',
]);

function normalizeText(value: unknown) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function isSectionLayoutMode(value: unknown): value is SectionLayoutMode {
  return value === 'composition' || value === 'flow';
}

function getSectionBlocks(settings: HomepageSectionSettings | undefined) {
  return Array.isArray(settings?.canvas?.blocks) ? settings!.canvas!.blocks : [];
}

function sectionLooksCompositionLedByLabel(section: SectionLike) {
  const label = normalizeText(section?.settings?.builderLabel);
  return (
    label === 'full screen' ||
    label === 'fullscreen' ||
    label.includes('full screen') ||
    label.includes('fullscreen') ||
    label.includes('immersive stage') ||
    label.includes('hero')
  );
}

function sectionHasImmersiveViewerBlock(section: SectionLike) {
  const blocks = getSectionBlocks(section?.settings);
  return blocks.some((block: any) => {
    if (block?.type !== 'model-viewer') return false;
    const content = block.content && typeof block.content === 'object' ? block.content : {};
    return normalizeText((content as any).surface) === 'immersive';
  });
}

function sectionHasFlowRootBlocks(section: SectionLike) {
  const blocks = getSectionBlocks(section?.settings);
  return blocks.some((block: any) => FLOW_ROOT_BLOCK_TYPES.has(block?.type));
}

function inferLegacySectionLayoutMode(section: SectionLike): SectionLayoutMode {
  if (section?.type !== 'canvas') return 'flow';
  if (
    section?.settings?.canvas?.presentation === 'immersive' ||
    sectionLooksCompositionLedByLabel(section) ||
    sectionHasImmersiveViewerBlock(section)
  ) {
    return 'composition';
  }
  return 'flow';
}

export function resolveSectionLayoutMode(section: SectionLike): SectionLayoutMode {
  const explicitMode = section?.settings?.layoutMode;
  if (isSectionLayoutMode(explicitMode)) return explicitMode;
  return inferLegacySectionLayoutMode(section);
}

export function isCompositionSection(section: SectionLike) {
  return resolveSectionLayoutMode(section) === 'composition';
}

export function isFlowSection(section: SectionLike) {
  return resolveSectionLayoutMode(section) === 'flow';
}

export function getSectionLayoutModePolicy(section: SectionLike): {
  selectable: boolean;
  lockedMode: SectionLayoutMode | null;
  reason: string | null;
} {
  if (!section || section.type !== 'canvas') {
    return {
      selectable: false,
      lockedMode: 'flow',
      reason: 'This section uses the normal responsive content flow.',
    };
  }

  if (section.settings?.canvas?.presentation === 'immersive' || sectionHasImmersiveViewerBlock(section)) {
    return {
      selectable: false,
      lockedMode: 'composition',
      reason: 'Fullscreen stage sections stay composition-led so the scene scales as one designed frame.',
    };
  }

  if (sectionHasFlowRootBlocks(section)) {
    return {
      selectable: false,
      lockedMode: 'flow',
      reason: 'Commerce, form, and utility blocks stay flow-rooted so they can reflow responsively.',
    };
  }

  return {
    selectable: true,
    lockedMode: null,
    reason: null,
  };
}
